/* eslint-disable max-classes-per-file */
import { DataFlowArgument } from './dataflow-argument';
import { IDataFlowDataType } from './dataflow-datatype';

export class CodePosition {
    public line: number;
    public column: number;
}

export class BasicExpression {
    public start: number;
    public stop: number;
    public text: string;
    public startPosition: CodePosition;
    public stopPosition: CodePosition;
    // In some cases like expression there is no real object backing up the expression.
    public referencedObject?: DataFlowArgument;
    public returnType: IDataFlowDataType;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public returnValue: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public value?: any;
}

export class ColumnIdentifier extends BasicExpression {
    public name: string;
}

export class ConfigName extends BasicExpression {
    public name: string;
}

export class Identifier extends BasicExpression {
    public name: string;
}

export class Keyword extends BasicExpression {
    public name: string;
}

export class BasicOperand extends BasicExpression {
    public basicName: BasicName;
    public indexLiteral?: number;
}

export class BasicName extends BasicExpression {
    public identifier?: Identifier;
    public keyword?: Keyword;
}

export class FunctionExpression extends BasicExpression {
    public basicName: BasicName;
    public children: Expression[]; // Of expression types only.
    public isCompileTimeExecutable: boolean;
}

export class MetaFunction extends BasicExpression {
    public basicName: BasicName;
    public children: BasicExpression[]; // Of expression and assignment type only.
}

export class TransformFunction extends BasicExpression {
    public transformName?: string;
    public groupName?: string;
    public basicName: BasicName;
    public children: Expression[]; // Of expression and assignment type only.
}

export class VariableReference extends BasicExpression {
    public basicName: BasicName;
}

export class ParameterReference extends BasicExpression {
    public parameterOperand: BasicName;
}

export class TemplateVariable extends BasicExpression {
    public basicName: BasicName;
}

export class MetaVariable extends BasicExpression {
    public name: string;
}

export class MetaMatch extends BasicExpression {
    public name: string;
}

export class MetaArgument extends BasicExpression {
    public name: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public arrayOrMapReference: any;
}

export class ColumnOperand extends BasicExpression {
    public transformName?: string;
    public groupName?: string;
    public name: BasicOperand;

    public getFullyQualifiedNameWhenBasicOperand(): string {
        return [this.transformName,
            this.groupName,
            (this.name.basicName.identifier
                // while typing this can be a keyword as well.
                || this.name.basicName.keyword).name].filter((value) => !!value).join('@');
    }
}

export class Cast extends BasicExpression {
    public expression: Expression;
    public inlineDatatype: Datatype;
}

export class Datatype extends BasicExpression {
    public format: string;
    public datatype: string;

    public get typestring(): string {
        return this.text;
    }

    public constructor(datatype: string) {
        super();
        this.datatype = datatype;
    }
}

export class ExternalDataType extends Datatype {
    public expression: Expression;

    constructor() {
        super('external');
    }
}

export class DecimalDatatype extends Datatype {
    public precision: number;
    public scale: number;

    constructor() {
        super('decimal');
    }
}

export class ComplexDatatype extends Datatype {
    public columnDefinitions: ColumnDefinition[] = [];

    constructor() {
        super('complex');
    }
}

export class MapDatatype extends Datatype {
    public keyType: Datatype;
    public valueType: Datatype;

    constructor() {
        super('map');
    }
}

export class ArrayDatatype extends Datatype {
    public arrayType: Datatype;

    constructor() {
        super('array');
    }
}

export class ColumnDefinition extends BasicExpression {
    public name: ColumnIdentifier;
    public datatype: Datatype;
}

export class TransientOperand extends BasicExpression {
    public name: BasicOperand;

    public getFullyQualifiedNameWhenBasicOperand(): string {
        return this.name && this.name.basicName ? [(this.name.basicName.identifier || this.name.basicName.keyword).name].filter((value) => !!value).join('@') : '';
    }
}

export class ParameterOperand extends BasicExpression {
    public basicName: BasicName;
}

export class ArrayType extends BasicExpression {
    public children: Expression[];
}

export class MapType extends BasicExpression {
    public keys: string[];
    public values: string;
    public children: Expression[];
}

export class Assignment extends BasicExpression {
    public columnIdentifier: ColumnIdentifier;
    public expression: Expression;
}

export class MetaAssignment extends BasicExpression {
    public expression: Expression;
}

export class Configuration extends BasicExpression {
    public configName: ConfigName;
    public expression: Expression;
}

export class Scope extends BasicExpression {
    public expression: Expression;
}

export class DecimalLiteral extends BasicExpression {
    public value: string;
}

export class HexLiteral extends BasicExpression {
    public value: string;
}

export class OctLiteral extends BasicExpression {
    public value: string;
}

export class BinaryLiteral extends BasicExpression {
    public value: string;
}

export class IntegerLiteral extends BasicExpression {
    public value: DecimalLiteral | HexLiteral | OctLiteral | BinaryLiteral;
}

export class FloatLiteralValue extends BasicExpression {
    public value: string;
}
export class HexfloatLiteral extends BasicExpression {
    public value: string;
}

export class FloatLiteral extends BasicExpression {
    public value: HexfloatLiteral | FloatLiteralValue;
}
export class StringLiteral extends BasicExpression {
    public value: string;
}
export class RegexLiteral extends BasicExpression {
    public value: string;
}

export class Interpolation extends BasicExpression {
    public value: string;
    public expressions: Expression[];
    public strings: StringLiteral[];
}

export class Literal extends BasicExpression {
    public value?: IntegerLiteral | FloatLiteral | StringLiteral | RegexLiteral;
}

export class BinaryOperator extends BasicExpression {
    public leftExpression: Expression;
    public rightExpression: Expression;
}

export type MulDivModOperatorType = 'multiply' | 'divide' | 'mod';

export class MulDivMod extends BinaryOperator {
    public operation: MulDivModOperatorType;
}

export type AddMinusType = 'plus' | 'minus';

export class AddMinus extends BinaryOperator {
    public operation: AddMinusType;
}

export type EqNotEqOperatorType = 'eq' | 'notEq' | 'eqNoCase' | 'eqNull';

export class EqNotEq extends BinaryOperator {
    public operation: EqNotEqOperatorType;
}

export type ComparisonOperatorType = 'gt' | 'lt' | 'gtEq' | 'ltEq';

export class Comparison extends BinaryOperator {
    public operation: ComparisonOperatorType;
}

export class And extends BinaryOperator {
}

export class Or extends BinaryOperator {
}

export class Xor extends BinaryOperator {
}

export class ArrayIndexer extends BinaryOperator {
}

export class FieldSelector extends BinaryOperator {
    public get fieldName(): BasicOperand {
        return this.rightExpression as unknown as BasicOperand;
    }

    public getFullPath(): string {
        return `${this.leftExpression.text}.${this.rightExpression.text}`;
    }
}

export class Struct extends BasicExpression {
    public assignments: Assignment[];
}

export class UnaryMinus extends BasicExpression {
    public expression: Expression;
}

export class Not extends BasicExpression {
    public expression: Expression;
}

export type ExpressionType = MulDivMod | AddMinus | EqNotEq |
    Comparison | And | Or | Scope | ColumnOperand | TemplateVariable | ParameterReference | VariableReference |
    ParameterReference | VariableReference | FunctionExpression | MetaFunction | Literal | UnaryMinus | Not |
    ArrayType | MapType | Struct | Interpolation | TransformFunction;
export class Expression extends BasicExpression {
    public value: ExpressionType;

    public get mulDivMod(): MulDivMod {
        return this.value as MulDivMod;
    }

    public set mulDivMod(value: MulDivMod) {
        this.value = value;
    }

    public get addMinus(): AddMinus {
        return this.value as AddMinus;
    }

    public set addMinus(value: AddMinus) {
        this.value = value;
    }

    public get eqNotEq(): EqNotEq {
        return this.value as EqNotEq;
    }

    public set eqNotEq(value: EqNotEq) {
        this.value = value;
    }

    public get comparison(): Comparison {
        return this.value as Comparison;
    }

    public set comparison(value: Comparison) {
        this.value = value;
    }

    public get and(): And {
        return this.value as And;
    }

    public set and(value: And) {
        this.value = value;
    }

    public get xor(): Xor {
        return this.value as Xor;
    }

    public set xor(value: Xor) {
        this.value = value;
    }

    public get or(): Or {
        return this.value as Or;
    }

    public set or(value: Or) {
        this.value = value;
    }

    public get scope(): Scope {
        return this.value as Scope;
    }

    public set scope(value: Scope) {
        this.value = value;
    }

    public get columnOperand(): ColumnOperand {
        return this.value as ColumnOperand;
    }

    public set columnOperand(value: ColumnOperand) {
        this.value = value;
    }

    public get cast(): Cast {
        return this.value as Cast;
    }

    public set cast(value: Cast) {
        this.value = value;
    }

    public get transientOperand(): TransientOperand {
        return this.value as TransientOperand;
    }

    public set transientOperand(value: TransientOperand) {
        this.value = value;
    }

    public get templateVariable(): TemplateVariable {
        return this.value as TemplateVariable;
    }

    public set templateVariable(value: TemplateVariable) {
        this.value = value;
    }

    public get parameterReference(): ParameterReference {
        return this.value as ParameterReference;
    }

    public set parameterReference(value: ParameterReference) {
        this.value = value;
    }

    public get variableReference(): VariableReference {
        return this.value as VariableReference;
    }
    public set variableReference(value: VariableReference) {
        this.value = value;
    }
    public get functionExpression(): FunctionExpression {
        return this.value as FunctionExpression;
    }
    public set functionExpression(value: FunctionExpression) {
        this.value = value;
    }
    public get metafunction(): MetaFunction {
        return this.value as MetaFunction;
    }
    public set metafunction(value: MetaFunction) {
        this.value = value;
    }
    public get transformFunction(): TransformFunction {
        return this.value as TransformFunction;
    }
    public set transformFunction(value: TransformFunction) {
        this.value = value;
    }

    public get literal(): Literal {
        return this.value as Literal;
    }

    public set literal(value: Literal) {
        this.value = value;
    }

    public get interpolation(): Interpolation {
        return this.value as Interpolation;
    }

    public set interpolation(value: Interpolation) {
        this.value = value;
    }

    public get metaVariable(): MetaVariable {
        return this.value as MetaVariable;
    }

    public set metaVariable(value: MetaVariable) {
        this.value = value;
    }

    public get metaMatch(): MetaMatch {
        return this.value as MetaMatch;
    }

    public set metaMatch(value: MetaMatch) {
        this.value = value;
    }

    public get metaArgument(): MetaArgument {
        return this.value as MetaArgument;
    }

    public set metaArgument(value: MetaArgument) {
        this.value = value;
    }

    public get unaryMinus(): UnaryMinus {
        return this.value as UnaryMinus;
    }

    public set unaryMinus(value: UnaryMinus) {
        this.value = value;
    }

    public get not(): Not {
        return this.value as Not;
    }

    public set not(value: Not) {
        this.value = value;
    }

    public get array(): ArrayType {
        return this.value as ArrayType;
    }

    public set array(value: ArrayType) {
        this.value = value;
    }

    public get map(): MapType {
        return this.value as MapType;
    }

    public set map(value: MapType) {
        this.value = value;
    }

    public get arrayIndexer(): ArrayIndexer {
        return this.value as ArrayIndexer;
    }

    public set arrayIndexer(value: ArrayIndexer) {
        this.value = value;
    }

    public get fieldSelector(): FieldSelector {
        return this.value as FieldSelector;
    }

    public set fieldSelector(value: FieldSelector) {
        this.value = value;
    }

    public get struct(): Struct {
        return this.value as Struct;
    }

    public set struct(value: Struct) {
        this.value = value;
    }
}
